/**
 * 学会的动作在宠物动作读口里的样子（A-2 TAUGHT-CATALOG-117，10-16 给 UI-2 的签名）：
 * GET /pets/{pet_id}/motion?purpose=home 的 PetMotionSet 里，教学段排在目录段后面，一单三段：
 *   taught_<单号>_enter：底姿（STAND／SIT／LIE）→ taught:<单号>
 *   taught_<单号>_main ：taught:<单号> → taught:<单号>，loop 真是循环、假是做一次
 *   taught_<单号>_exit ：taught:<单号> → 回到同一个底姿
 * 认教学段按段名的键族，不按头部 clip_catalog_version（那是领头段 stand_idle 的）。
 * 一单能播：三段都在，而且底姿有待机段（同一个状态进出、循环）。预览照现成 PetMotion：底姿待机 → enter → main（循环的放几圈）→ exit。
 */
import type { PetMotionClip, PetMotionSet } from "@/shared/contracts";

export const taughtKey = (orderId: string) => `taught:${orderId}`;
export const taughtClipKey = (orderId: string, part: "enter" | "main" | "exit") => `taught_${orderId}_${part}`;

export interface TaughtClips {
  basePose: string;
  idle: PetMotionClip;
  enter: PetMotionClip;
  main: PetMotionClip;
  exit: PetMotionClip;
}

/** 这一单能播就给三段和底姿待机；缺一样都不给（页面照实说「还在准备」，不拿别的动作顶）。 */
export function taughtClips(set: PetMotionSet | null | undefined, orderId: string): TaughtClips | null {
  if (!set) return null;
  const by = (key: string) => set.clips.find((clip) => clip.clip_key === key);
  const enter = by(taughtClipKey(orderId, "enter"));
  const main = by(taughtClipKey(orderId, "main"));
  const exit = by(taughtClipKey(orderId, "exit"));
  if (!enter || !main || !exit) return null;
  const state = taughtKey(orderId);
  const basePose = enter.from_state;
  if (enter.to_state !== state || main.from_state !== state || main.to_state !== state || exit.from_state !== state || exit.to_state !== basePose) return null;
  const idle = set.clips.find((clip) => clip.from_state === basePose && clip.to_state === basePose && clip.loop && !clip.clip_key.startsWith("taught_"));
  if (!idle) return null;
  return { basePose, idle, enter, main, exit };
}

/** 预览的播放顺序：底姿待机一遍 → 进入 → 主体（循环的放 3 圈，一次性的 1 遍）→ 退出 → 回到待机。 */
export function previewSequence(clips: TaughtClips, loops = 3): PetMotionClip[] {
  const mains = Array.from({ length: clips.main.loop ? Math.max(1, loops) : 1 }, () => clips.main);
  return [clips.idle, clips.enter, ...mains, clips.exit, clips.idle];
}
